import type { Config, DashboardWidgets, LegacyEnvWidgetConfig } from '../types';

export type WidgetConfigSource = 'api' | 'env' | 'cache';

/** QA: log which path supplied widget config (api | cache | env). */
export function logWidgetConfigSource(source: WidgetConfigSource): void {
  console.log(`[widget-config] configSource: ${source}`);
}

/** User id for config-service from Keycloak (before PIM getUserInfo). */
export function getKeycloakConfigUserId(
  keycloak: Keycloak.KeycloakInstance,
): string | undefined {
  const parsed = keycloak.tokenParsed as
    | { email?: string; preferred_username?: string }
    | undefined;
  return parsed?.email ?? parsed?.preferred_username;
}

export function emptyDashboardWidgets(): DashboardWidgets {
  return { layout: [], palette: [] };
}

/**
 * Map one optional API dashboard section to {@link DashboardWidgets}.
 * Missing section → empty; present but empty (`{}`) → empty arrays.
 */
export function sectionFromApi(
  section: mumms.DashboardWidgetsApiSection | undefined,
): DashboardWidgets {
  if (!section) {
    return emptyDashboardWidgets();
  }
  return {
    layout: section.layout ?? [],
    palette: section.palette ?? [],
  };
}

/**
 * Build a full {@link Config} from GET /api/widgets/config.
 * The API returns **exactly one** of `patientDashboard` | `clinicalDashboard`
 * (patient chart vs clinical). The absent key is filled with empty widgets so
 * App always has both slots on {@link Config.widgets}.
 */
export function configFromApiResponse(
  api: mumms.WidgetsConfigApiResponse,
): Config {
  return {
    configSource: 'api',
    widgets: {
      patientDashboard: sectionFromApi(api.patientDashboard),
      clinicalDashboard: sectionFromApi(api.clinicalDashboard),
    },
  };
}

/**
 * Apply a single-dashboard GET body onto existing config.
 *
 * Contract: `api` has **one** of `patientDashboard` | `clinicalDashboard`, never
 * both. That section is replaced; the other dashboard is left from `existing`
 * (or empty if none). Prefer this over {@link configFromApiResponse} when
 * refreshing so a patient-only response does not wipe clinical (and vice versa).
 */
export function mergeWidgetConfigFromApi(
  existing: Config | null | undefined,
  api: mumms.WidgetsConfigApiResponse,
): Config {
  const base: Config = existing ?? {
    configSource: 'api',
    widgets: {
      patientDashboard: emptyDashboardWidgets(),
      clinicalDashboard: emptyDashboardWidgets(),
    },
  };

  const widgets = { ...base.widgets };
  if (api.patientDashboard !== undefined) {
    widgets.patientDashboard = sectionFromApi(api.patientDashboard);
  } else if (api.clinicalDashboard !== undefined) {
    widgets.clinicalDashboard = sectionFromApi(api.clinicalDashboard);
  }

  return {
    ...base,
    configSource: 'api',
    widgets,
  };
}

/** Env fallback: flat widget lists as layout; no add-widget palette. */
export function configFromEnvWidgetConfig(env: LegacyEnvWidgetConfig): Config {
  return {
    configSource: 'env',
    widgets: {
      patientDashboard: {
        layout: env.widgets.patientDashboard ?? [],
        palette: [],
      },
      clinicalDashboard: {
        layout: env.widgets.clinicalDashboard ?? [],
        palette: [],
      },
    },
  };
}

function isHttpErrorResponse(
  value: mumms.WidgetsConfigApiResponse | Response,
): value is Response {
  return typeof Response !== 'undefined' && value instanceof Response;
}

export function resolveWidgetConfigFromSources(
  apiResponse: mumms.WidgetsConfigApiResponse | Response,
  envFallback: LegacyEnvWidgetConfig | null | undefined,
): { config: Config | null; configSource: WidgetConfigSource | null } {
  if (!isHttpErrorResponse(apiResponse)) {
    return {
      config: configFromApiResponse(apiResponse),
      configSource: 'api',
    };
  }

  if (envFallback?.widgets) {
    return {
      config: configFromEnvWidgetConfig(envFallback),
      configSource: 'env',
    };
  }

  return { config: null, configSource: null };
}
